import React, { useCallback, useContext, useState } from 'react';
import { AppContext } from '@prisjakt/ui-app-context';
import { ExpandedIndicator } from '@prisjakt/ui-expandable-group';
import { parseNodes } from 'src/components/data-list/components/parseNodes';
import { No, Yes } from 'src/components/data-list/components/colored-icon';
import { desktopMaxColumns } from '../../../../constants';
import { getProductProp, getProductListProps } from '../../../../utils';
import { PropertyBoolean } from '../../../../../../types';
import { Product, Property } from '../../../../types';

import * as S from './styles';

type OwnProps = {
  defaultOpen?: boolean;
  highlightDifferences?: boolean;
  isDummySection?: boolean;
  isOpen?: boolean;
  products?: Product[];
  properties?: (string | null)[] | null;
  title?: string | null;
  shouldAnimate?: boolean;
  sectionId?: string | null;
};

const Section = ({
  defaultOpen,
  highlightDifferences,
  isDummySection,
  isOpen,
  products,
  properties,
  title,
  shouldAnimate,
  sectionId,
}: OwnProps) => {
  const { t } = useContext(AppContext);
  const [open, setOpen] = useState<boolean>(Boolean(defaultOpen));
  const sectionIsOpen = isOpen !== undefined ? isOpen : open;
  const allProps = products
    ? (getProductListProps(products) as Property[])
    : [];

  const handleClick = useCallback(() => {
    setOpen(!sectionIsOpen);
  }, [sectionIsOpen]);

  const renderPropertyValue = (property?: Property) => {
    if (!property) {
      return null;
    }
    const parsed = parseNodes(property, t, true);
    if (
      property.type === 'boolean' &&
      (property as PropertyBoolean).boolean !== null
    ) {
      return (property as PropertyBoolean).boolean ? <Yes /> : <No />;
    }
    return parsed.value!.text;
  };

  const renderProperty = (
    property: Property,
    depth: number
  ): React.ReactNode => {
    if (!property || property.type === 'link') {
      return null;
    }

    const children =
      Array.isArray(property.children) && property.children.length
        ? property.children
            .filter(id => id)
            .map(id => allProps!.find(item => item.id === id!))
        : [];
    const row = (
      <S.PropertyList>
        <S.PropertyHeader>
          <S.PropertyHeaderLabel depth={depth}>
            {property.name}
          </S.PropertyHeaderLabel>
        </S.PropertyHeader>
        {products!.map((p, index) => {
          const productProperty = getProductProp(p, property.id);
          const value = renderPropertyValue(productProperty);
          return (
            <React.Fragment key={`property-wrapper-${p.id}`}>
              <S.Property
                key={`property-${p.id}`}
                isDiff={
                  highlightDifferences &&
                  productProperty &&
                  productProperty.isDiff
                }
                desktopScaleToFit={
                  products && products.length >= desktopMaxColumns
                }
                shouldAnimate={
                  products && index + 1 === products.length && shouldAnimate
                }
              >
                {value === '' && depth > 0 ? '' : value}
              </S.Property>
              {products!.length === 1 && <S.Property> </S.Property>}
            </React.Fragment>
          );
        })}
      </S.PropertyList>
    );
    const childRows = children.length
      ? children.map(child => child && renderProperty(child, depth + 1))
      : null;

    return row || childRows ? (
      <React.Fragment key={`_property_row_${property.id}`}>
        {row}
        {childRows}
      </React.Fragment>
    ) : null;
  };

  const rows =
    properties &&
    properties.map(propertyId => {
      if (!propertyId) return undefined;
      const property =
        allProps && allProps.find(item => item.id === propertyId!);

      return property && renderProperty(property, 0);
    });

  const renderDummySection = () => (
    <React.Fragment key={`dummy-section-${sectionId}`}>
      <S.Section open hasMaxWidth={!products || products.length === 0}>
        <S.DummySectionHeader />
      </S.Section>
      {[1, 2, 3].map(item => (
        <S.PropertyList
          key={`dummy-section-${item}`}
          isDummy
          hasMaxWidth={!products || products.length === 0}
        >
          <S.PropertyHeader />
          <S.Property> </S.Property>
        </S.PropertyList>
      ))}
    </React.Fragment>
  );

  return isDummySection || !products ? (
    renderDummySection()
  ) : (
    <React.Fragment key={`column-section-${sectionId}`}>
      <S.SectionHeaderButton type="button" onClick={handleClick}>
        <S.Section open={open}>
          {products.map((item, index) => {
            return (
              <React.Fragment key={`product-${item.id}`}>
                {index === 0 && (
                  <S.TitleWrapper>
                    <S.Title>
                      <ExpandedIndicator expanded={sectionIsOpen} />
                      {title}
                    </S.Title>
                  </S.TitleWrapper>
                )}
                <S.SectionHeader
                  desktopScaleToFit={products.length >= desktopMaxColumns}
                  shouldAnimate={index + 1 === products.length && shouldAnimate}
                />
              </React.Fragment>
            );
          })}

          {products.length === 1 && <S.DummySectionHeader />}
        </S.Section>
      </S.SectionHeaderButton>
      {sectionIsOpen ? rows : null}
    </React.Fragment>
  );
};

export default Section;
